import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { HERO_LIST, HEROES, type HeroDef, type HeroId, Role, ROLE_NAMES, Slot } from "@rivalwatch/shared";
import { heroTheme } from "../audio/ui.ts";
import { currentGame } from "./GameView.tsx";
import { AbilityIcon, RoleIcon } from "./icons.tsx";
import { HeroPortrait } from "./Portrait.tsx";
import { ui } from "./store.ts";

const ROLES: readonly Role[] = [Role.Tank, Role.Damage, Role.Support];
const ROLE_LIMIT = 2;
const NAV_COLS = 6;
const SLOT_ORDER = [Slot.Primary, Slot.Secondary, Slot.Ability1, Slot.Ability2, Slot.Ability3, Slot.Ultimate] as const;

function allPassives(def: HeroDef) {
  return [...(def.passive ? [def.passive] : []), ...(def.passives ?? [])];
}

export function HeroSelect({ onClose, onSelect }: { onClose: () => void; onSelect?: (hero: HeroId) => void }) {
  const l = ui.lobby.value;
  const me = l?.players.find((p) => p.id === ui.you.value);
  const current = me?.hero;
  const roster = useMemo(() => ROLES.flatMap((role) => HERO_LIST.filter((h) => h.role === role)), []);
  /** Keyboard/gamepad selection; the mouse picks on click, so hovering only previews. */
  const [sel, setSel] = useState<HeroId>(current ?? roster[0]!.id);
  const [hover, setHover] = useState<HeroId>();
  const shown = hover ?? sel;
  const root = useRef<HTMLDivElement>(null);
  const cards = useRef(new Map<HeroId, HTMLButtonElement>());

  useEffect(() => {
    if (current !== undefined) setSel(current);
  }, [current]);

  const def = HEROES[shown];
  const mates = l && me ? l.players.filter((p) => p.team === me.team && p.id !== me.id) : [];
  const counts = {
    hero: (h: HeroId) => mates.filter((p) => p.hero === h).length,
    role: (role: Role) => mates.filter((p) => HEROES[p.hero].role === role).length,
  };
  const availability = (h: HeroId) => {
    if (!l || !me) return { ok: true, reason: "Available" };
    if (l.settings.heroLimit > 0 && counts.hero(h) >= l.settings.heroLimit) {
      return { ok: false, reason: l.settings.heroLimit === 1 ? "Taken by teammate" : `Team limit ${l.settings.heroLimit}/${l.settings.heroLimit}` };
    }
    const role = HEROES[h].role;
    if (l.settings.roleLock && counts.role(role) >= ROLE_LIMIT) return { ok: false, reason: `${ROLE_NAMES[role]} slots full` };
    return { ok: true, reason: current === h ? "Current hero" : "Available" };
  };
  const shownAvailability = availability(shown);
  const selAvailability = availability(sel);
  const hp = def.health + def.armor + def.shields;

  const setCardRef = (h: HeroId) => (el: HTMLButtonElement | null) => {
    if (el) cards.current.set(h, el);
    else cards.current.delete(h);
  };
  const pick = (h: HeroId, cue = false, focus = false) => {
    if (h !== sel && cue) heroTheme(h, "tease");
    setSel(h);
    if (focus) requestAnimationFrame(() => cards.current.get(h)?.focus());
  };
  const move = (delta: number) => {
    const i = roster.findIndex((h) => h.id === sel);
    const next = roster[(i + delta + roster.length) % roster.length]!;
    pick(next.id, true, true);
  };
  /** Plays `h` (the keyboard selection by default) and closes; false when it's unavailable. */
  const choose = (h: HeroId = sel) => {
    const a = availability(h);
    if (!a.ok && current !== h) return false;
    currentGame()?.send({ t: "setHero", hero: h });
    if (current !== h) onSelect?.(h);
    heroTheme(h, "heroic");
    onClose();
    return true;
  };

  useEffect(() => {
    let raf = 0;
    let last = 0;
    let held = "";
    const poll = (now: number) => {
      const pad = navigator.getGamepads?.().find(Boolean);
      if (pad) {
        const x = pad.axes[0] ?? 0;
        const y = pad.axes[1] ?? 0;
        const dir = pad.buttons[15]?.pressed || x > 0.55 ? "r" : pad.buttons[14]?.pressed || x < -0.55 ? "l" : pad.buttons[13]?.pressed || y > 0.55 ? "d" : pad.buttons[12]?.pressed || y < -0.55 ? "u" : "";
        const accept = !!pad.buttons[0]?.pressed;
        const cancel = !!pad.buttons[1]?.pressed;
        const key = accept ? "a" : cancel ? "b" : dir;
        if (key && (key !== held || now - last > 220)) {
          held = key;
          last = now;
          if (key === "l") move(-1);
          else if (key === "r") move(1);
          else if (key === "u") move(-NAV_COLS);
          else if (key === "d") move(NAV_COLS);
          else if (key === "a") choose();
          else if (key === "b") onClose();
        } else if (!key) held = "";
      }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [sel, l, me, current]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft") {
      e.preventDefault();
      move(-1);
    } else if (e.code === "ArrowRight") {
      e.preventDefault();
      move(1);
    } else if (e.code === "ArrowUp") {
      e.preventDefault();
      move(-NAV_COLS);
    } else if (e.code === "ArrowDown") {
      e.preventDefault();
      move(NAV_COLS);
    } else if (e.code === "Home") {
      e.preventDefault();
      pick(roster[0]!.id, true, true);
    } else if (e.code === "End") {
      e.preventDefault();
      pick(roster.at(-1)!.id, true, true);
    } else if (e.code === "Enter" || e.code === "Space") {
      e.preventDefault();
      choose();
    } else if (e.code === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div class="overlay hero-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="hero-select plate" ref={root} onKeyDown={onKeyDown} aria-label="Hero select">
        <header class="hero-select-head">
          <div>
            <span class="hero-kicker">Roster contact sheet</span>
            <h2>Choose your hero</h2>
          </div>
          <div class="hero-select-rules" aria-label="Lobby hero rules">
            <span>{l?.settings.roleLock ? "Role lock: 2 per role" : "Open roles"}</span>
            <span>{l?.settings.heroLimit ? `Hero limit: ${l.settings.heroLimit}` : "Duplicates allowed"}</span>
            <span>Swap in spawn or while dead</span>
          </div>
        </header>

        <div class="hero-select-body">
          <section class="hero-roster" aria-label="Heroes grouped by role" onMouseLeave={() => setHover(undefined)}>
            {ROLES.map((role) => {
              const heroes = HERO_LIST.filter((h) => h.role === role);
              const roleCount = counts.role(role);
              return (
                <div class={`hero-role-group r${role}`} key={role}>
                  <h3>
                    <span>
                      <RoleIcon role={role} /> {ROLE_NAMES[role]}
                    </span>
                    <small>
                      {heroes.length} heroes{l?.settings.roleLock ? ` · ${Math.min(roleCount, ROLE_LIMIT)}/${ROLE_LIMIT} filled` : ""}
                    </small>
                  </h3>
                  <div class="hero-grid">
                    {heroes.map((h) => {
                      const a = availability(h.id);
                      const locked = !a.ok && current !== h.id;
                      return (
                        <button
                          key={h.id}
                          ref={setCardRef(h.id)}
                          class={`hero-tile${sel === h.id ? " sel" : ""}${current === h.id ? " cur" : ""}${locked ? " locked" : ""}`}
                          style={{ "--hc": h.color }}
                          aria-label={`${h.name}, ${ROLE_NAMES[h.role]}. ${a.reason}`}
                          aria-pressed={sel === h.id}
                          aria-disabled={locked}
                          title={`${h.name} — ${a.reason}`}
                          data-sound="none"
                          onMouseEnter={() => setHover(h.id)}
                          onFocus={() => pick(h.id)}
                          onClick={() => {
                            if (!choose(h.id)) pick(h.id, true);
                          }}
                        >
                          <HeroPortrait hero={h.id} />
                          <span class="hero-tile-name">{h.name}</span>
                          {(current === h.id || locked) && <span class="hero-tile-state">{current === h.id ? "Playing" : a.reason}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>

          <section class="hero-dossier" style={{ "--hc": def.color }} aria-label={`${def.name} details`}>
            <HeroPortrait hero={shown} class="hero-dossier-bust" />
            <div class="hero-dossier-top">
              <span class={`role-pill r${def.role}`}>
                <RoleIcon role={def.role} /> {ROLE_NAMES[def.role]}
              </span>
              <span class={`hero-availability ${shownAvailability.ok || current === shown ? "ok" : "blocked"}`}>{current === shown ? "Current hero" : shownAvailability.reason}</span>
            </div>
            <h3>{def.name}</h3>
            <p class="hero-blurb">{def.blurb}</p>
            <div class="hero-statline" aria-label="Hero stats">
              <span>
                <b>{hp}</b> total
              </span>
              {def.armor > 0 && (
                <span>
                  <b>{def.armor}</b> armor
                </span>
              )}
              {def.shields > 0 && (
                <span>
                  <b>{def.shields}</b> shields
                </span>
              )}
              <span>
                <b>{def.speed.toFixed(1)}</b> m/s
              </span>
              {def.ammo > 0 && (
                <span>
                  <b>{def.ammo}</b> ammo
                </span>
              )}
            </div>
            <div class="hero-kit" aria-label={`${def.name} abilities`}>
              {SLOT_ORDER.flatMap((s) => {
                const a = def.abilities[s];
                if (!a) return [];
                return (
                  <article class="hero-ability" key={s} title={a.description}>
                    <kbd>{a.key}</kbd>
                    <span class="hero-ability-icon">
                      <AbilityIcon name={a.name} hero={def.id} slot={s} size={22} />
                    </span>
                    <div>
                      <b>{a.name}</b>
                      {a.cooldown > 0 && <em>{a.cooldown}s{a.charges ? ` · ${a.charges} charges` : ""}</em>}
                      <p>{a.description}</p>
                    </div>
                  </article>
                );
              })}
              {allPassives(def).map((p, i) => (
                <article class="hero-ability passive" key={`p${i}`} title={p.description}>
                  <kbd>Passive</kbd>
                  <span class="hero-ability-icon">
                    <AbilityIcon name={p.name} hero={def.id} slot={i === 0 ? "passive" : undefined} size={22} />
                  </span>
                  <div>
                    <b>{p.name}</b>
                    <p>{p.description}</p>
                  </div>
                </article>
              ))}
            </div>
            <div class="hero-actions">
              <button class="btn brass" data-sound="none" onClick={() => choose()} disabled={!selAvailability.ok && current !== sel}>
                <span>{current === sel ? `Keep ${HEROES[sel].name}` : `Play ${HEROES[sel].name}`}</span>
              </button>
              <button class="btn ghost" onClick={onClose}>
                <span>Back</span>
              </button>
            </div>
            <p class="hero-help">
              <kbd>Click</kbd> or <kbd>Enter</kbd> pick · <kbd>Arrows</kbd> move · <kbd>Esc</kbd> back · <kbd>Gamepad</kbd> D-pad/A/B
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
